/** Teaching: invitations, students and teachers, access to private content. */
import { baseApi } from '../baseApi';
import type {
  CatalogItem,
  Grants,
  Invite,
  InvitePreview,
  Student,
  Teacher,
} from '../types';
export const teachingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getShared: build.query<CatalogItem[], string>({
      query: (lang) => ({ url: '/catalog/shared', params: { lang } }),
      providesTags: ['Content', 'Teaching'],
    }),
    listInvites: build.query<Invite[], void>({
      query: () => '/teaching/invites',
      providesTags: ['Teaching'],
    }),
    createInvite: build.mutation<Invite, void>({
      query: () => ({ url: '/teaching/invites', method: 'POST' }),
      invalidatesTags: ['Teaching'],
    }),
    revokeInvite: build.mutation<void, string>({
      query: (code) => ({ url: `/teaching/invites/${code}`, method: 'DELETE' }),
      invalidatesTags: ['Teaching'],
    }),
    // Not tagged: accepting an invitation spends it, and asking again what it says
    // would find nothing where the page should say "you have joined".
    previewInvite: build.query<InvitePreview, string>({
      query: (code) => `/invites/${code}`,
    }),
    acceptInvite: build.mutation<Teacher, string>({
      query: (code) => ({ url: `/invites/${code}/accept`, method: 'POST' }),
      invalidatesTags: ['Teaching'],
    }),
    listStudents: build.query<Student[], void>({
      query: () => '/teaching/students',
      providesTags: ['Teaching'],
    }),
    removeStudent: build.mutation<void, string>({
      query: (id) => ({ url: `/teaching/students/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Teaching'],
    }),
    listMyTeachers: build.query<Teacher[], void>({
      query: () => '/me/teachers',
      providesTags: ['Teaching'],
    }),
    // Leaving a teacher also takes away the private content they shared.
    leaveTeacher: build.mutation<void, string>({
      query: (id) => ({ url: `/me/teachers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Teaching', 'Content'],
    }),
    // What is shared is named by its address: `author/course`, or `author/-/lesson`.
    getGrants: build.query<Grants, string>({
      query: (id) => `/teaching/grants/${id}`,
      providesTags: ['Teaching'],
    }),
    setGrants: build.mutation<Grants, { id: string; studentIds: string[] }>({
      query: ({ id, studentIds }) => ({
        url: `/teaching/grants/${id}`,
        method: 'PUT',
        body: { studentIds },
      }),
      invalidatesTags: ['Teaching'],
    }),
  }),
});

export const {
  useGetSharedQuery,
  useListInvitesQuery,
  useCreateInviteMutation,
  useRevokeInviteMutation,
  usePreviewInviteQuery,
  useAcceptInviteMutation,
  useListStudentsQuery,
  useRemoveStudentMutation,
  useListMyTeachersQuery,
  useLeaveTeacherMutation,
  useGetGrantsQuery,
  useSetGrantsMutation,
} = teachingApi;
